import React, { useMemo } from "react";
import { TooltipProvider } from "src/shadcn/ui/tooltip";
import { TransitPurchaseOrder } from "src/types";
import { MetricLine } from "./metricLine";

interface TransitPurchaseOrderFulfilmentCompletionCellProps {
  transitPurchaseOrder: TransitPurchaseOrder;
  canViewWarehouseProgress: boolean;
}

export const TransitPurchaseOrderFulfilmentCompletionCell: React.FC<
  TransitPurchaseOrderFulfilmentCompletionCellProps
> = ({ transitPurchaseOrder, canViewWarehouseProgress }) => {
  const fulfilmentRate = useMemo(() => {
    if (typeof transitPurchaseOrder.confirmationDetails?.fulfilmentRate === "number") {
      return transitPurchaseOrder.confirmationDetails.fulfilmentRate;
    }
    const orderedTotal =
      transitPurchaseOrder.products?.reduce((sum, product) => sum + (product.orderedQuantity ?? 0), 0) ?? 0;
    const confirmedTotal =
      transitPurchaseOrder.products?.reduce((sum, product) => sum + (product.confirmedQuantity ?? 0), 0) ?? 0;
    return orderedTotal > 0 ? (Math.min(confirmedTotal, orderedTotal) / orderedTotal) * 100 : 0;
  }, [transitPurchaseOrder.confirmationDetails?.fulfilmentRate, transitPurchaseOrder.products]);

  const warehouseMetrics = useMemo(() => {
    if (!canViewWarehouseProgress) {
      return {
        completionRate: 0
      };
    }

    const completionRate =
      typeof transitPurchaseOrder.warehouseCompletionRate === "number"
        ? transitPurchaseOrder.warehouseCompletionRate
        : 0;

    return {
      completionRate
    };
  }, [canViewWarehouseProgress, transitPurchaseOrder.warehouseCompletionRate]);

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex min-w-[200px] flex-col">
        <MetricLine
          label="Supplier fulfilment"
          tooltip="Tracks how much of the originally ordered quantity has been confirmed by the supplier."
          percentage={fulfilmentRate}
          decimals={1}
        />
        <MetricLine
          label="Warehouse completion"
          tooltip="Measures how much of the expected quantity has physically arrived and been checked in by the warehouse."
          percentage={warehouseMetrics.completionRate}
          decimals={1}
        />
      </div>
    </TooltipProvider>
  );
};
